import {
  Avatar,
  Divider,
  Grid,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { ITreatmentEntitlementDTO } from 'API/Treatment/treatment';
import { dotDateFormat, shorterDateFormat } from 'Config';
import { addYears, endOfMonth, format, isBefore, set } from 'date-fns';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleAlert } from 'Images';

interface IEntitlement {
  data?: ITreatmentEntitlementDTO[];
}

export const Entitlement = ({ data }: IEntitlement) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Treatment);
  const isDesktop = useIsDesktop();

  const permanentlyEndDates = ['31.12.2099', '31.12.2999'];

  const getDatesInRange = (startDateEntitlement: Date) => {
    return dictionary.entitlementPeriodDates(
      format(new Date(startDateEntitlement), shorterDateFormat),
      format(
        endOfMonth(
          addYears(
            set(new Date(startDateEntitlement), { month: 1, date: 1 }),
            1
          )
        ),
        shorterDateFormat
      )
    );
  };

  return (
    <Stack gap={isDesktop ? '16px' : '12px'}>
      <Typography component={'h2'} variant={'h5'} color={'text.secondary'}>
        {dictionary.entitlement}
      </Typography>
      <List
        sx={{
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: isDesktop ? '24px' : '20px',
        }}
      >
        {data &&
          data.map((entitlement: ITreatmentEntitlementDTO, index: number) => {
            return (
              <ListItem key={index} sx={{ padding: 0, margin: 0 }}>
                <Stack
                  bgcolor='#FFF'
                  padding={isDesktop ? '32px 24px' : '16px 16px 24px 16px'}
                  border={'1px solid'}
                  borderColor={'primary.shade_100'}
                  borderRadius={'16px'}
                  gap={isDesktop ? '16px' : '12px'}
                >
                  <Stack gap={isDesktop ? '4px' : '6px'}>
                    <Typography variant={'body7'}>
                      {dictionary.entitlementType}
                    </Typography>
                    <Typography variant={'body4'}>
                      {entitlement.catalogNumberDescription}
                    </Typography>
                  </Stack>

                  <Divider
                    aria-hidden={'true'}
                    sx={{ borderColor: 'primary.shade_100' }}
                  />

                  <Grid container gap={isDesktop ? '56px' : '20px 0'}>
                    {entitlement.endDateEntitlement && (
                      <Grid item xs={7} sm={2.5}>
                        <Stack gap={'4px'}>
                          <Typography variant={'body7'}>
                            {dictionary.entitlementPeriod}
                          </Typography>
                          <Typography variant={'body5'}>
                            {getDatesInRange(entitlement.daysBalanceStartDate)}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {entitlement.entitlementDays && (
                      <Grid item xs={5} sm={1.5}>
                        <Stack gap={'4px'}>
                          <Typography variant={'body7'}>
                            {dictionary.entitlementNights}
                          </Typography>
                          <Typography variant={'body5'}>
                            {dictionary.entitlementNightsBalance(
                              entitlement.entitlementDays,
                              entitlement.daysBalance
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}

                    {entitlement.accompanied && (
                      <Grid item xs={7} sm={1.5}>
                        <Stack gap={'4px'}>
                          <Typography variant={'body7'}>
                            {dictionary.entitlementAttendants}
                          </Typography>
                          <Typography variant={'body5'}>
                            {entitlement.accompanied}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {entitlement.endDateEntitlement && (
                      <Grid item xs={5} sm={2}>
                        <Stack gap={'4px'}>
                          <Typography variant={'body7'}>
                            {dictionary.entitlementEndingDate}
                          </Typography>
                          <Typography variant={'body5'}>
                            {permanentlyEndDates.includes(
                              format(
                                new Date(entitlement.endDateEntitlement),
                                dotDateFormat
                              )
                            )
                              ? dictionary.permanently
                              : format(
                                  new Date(entitlement.endDateEntitlement),
                                  dotDateFormat
                                )}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                  {entitlement.displayRestrictionMessage && (
                    <>
                      <Divider
                        aria-hidden={'true'}
                        sx={{ borderColor: 'primary.shade_100' }}
                      />
                      <Stack direction='row' gap={'8px'}>
                        <Avatar
                          src={PurpleAlert}
                          sx={{
                            width: isDesktop ? '24px' : '20px',
                            height: isDesktop ? '24px' : '20px',
                          }}
                          alt={''}
                        />
                        <Stack
                          gap={isDesktop ? '8px' : '4px'}
                          maxWidth={isDesktop ? 'initial' : '250px'}
                        >
                          <Typography variant={'body6'}>
                            {dictionary.entitlementRestrictionText1}
                          </Typography>
                          <Typography variant={'body5'}>
                            {dictionary.entitlementRestrictionText2}
                          </Typography>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Stack>
              </ListItem>
            );
          })}
      </List>
    </Stack>
  );
};
